/*
 * Pure helpers for the HTML tearsheet surface.
 *
 * These live outside tearsheet-v2.tsx so they can be unit-tested without
 * mounting the component (and so the component file keeps exporting only
 * components, which is what react-refresh wants).
 */

/** Marks the inline editor's stylesheet so it can be found and removed again. */
export const EDITOR_STYLE_MARKER = "tearsheet-v2-editor";

/**
 * A document the inline editor has already wired up.
 *
 * The flag is an expando property rather than a `data-` attribute
 * precisely because properties don't serialize. The save path writes the
 * document's own markup back to the database, so an attribute marker
 * would be persisted, seen on the next load, and would short-circuit
 * attachment forever — leaving fields that still carry `contenteditable`
 * (so still look and behave editable) with nothing listening, and every
 * subsequent edit silently lost.
 */
export type EditableDocument = Document & {
  __tearsheetEditorAttached?: boolean;
};

/**
 * Removes everything the inline editor adds to a document: the
 * contenteditable/spellcheck attributes, its hover-and-focus stylesheet,
 * and the legacy attribute-based attachment marker.
 *
 * Takes the root element rather than the Document so it can operate on a
 * detached clone, leaving the live (still-being-edited) DOM untouched.
 */
export function stripEditorArtifacts(root: HTMLElement): void {
  root.removeAttribute("data-tearsheet-editor-attached");
  root
    .querySelectorAll(`[data-injected-by="${EDITOR_STYLE_MARKER}"]`)
    .forEach((el) => el.remove());
  root.querySelectorAll("[contenteditable]").forEach((el) => {
    el.removeAttribute("contenteditable");
    el.removeAttribute("spellcheck");
  });
}

/**
 * The document's markup with the editor's scaffolding removed — what
 * both saving and printing should use.
 *
 * Serializing the live document instead would bake the editor into the
 * AI worker's HTML: permanently for a save, and into the PDF (blue focus
 * outlines and all) for a print.
 */
export function serializeCleanDocument(doc: Document): string {
  const clean = doc.documentElement.cloneNode(true) as HTMLElement;
  stripEditorArtifacts(clean);
  return "<!DOCTYPE html>\n" + clean.outerHTML;
}

/**
 * `YYYY-MM-DD` for a filename, tolerating a missing or unparseable date.
 *
 * `new Date(undefined).toISOString()` throws a RangeError, which used to
 * fail the whole download for any entry whose `created_on` didn't come
 * back from the API.
 */
export function fileDatePart(value: string | Date | undefined): string {
  const time = value ? new Date(value).getTime() : NaN;
  if (Number.isNaN(time)) return "undated";
  return new Date(time).toISOString().split("T")[0];
}

/**
 * The width the AI schema is authored against — its root page node
 * hard-codes `maxWidth: 1100px`, and the full-page view caps to match.
 */
export const AUTHORED_TEARSHEET_WIDTH = 1100;

/** Classes that opt a subtree into the narrow-panel reflow in index.css. */
const REFLOW_CLASSES = ["tearsheet-panel-content", "tearsheet-reflow-content"];

/**
 * Temporarily lays a tearsheet subtree out at its authored width so a
 * screenshot capture doesn't inherit the side panel's collapsed layout.
 *
 * Mutates in place rather than working on a clone so that chart canvases
 * keep their painted contents — a cloned <canvas> is blank — and so that
 * measure-then-render charts (recharts' ResponsiveContainer) get a chance
 * to re-render at the capture width before they are serialized.
 *
 * `width` defaults to the tearsheet's authored width; the shared report
 * PDF pipeline passes the printable width of the target paper instead.
 *
 * Returns a function that puts everything back; always call it.
 */
export function forceFullWidthLayout(
  root: HTMLElement,
  width: number = AUTHORED_TEARSHEET_WIDTH,
): () => void {
  const undo: Array<() => void> = [];

  const prevWidth = root.style.width;
  const prevMinWidth = root.style.minWidth;
  root.style.width = `${width}px`;
  root.style.minWidth = `${width}px`;
  undo.push(() => {
    root.style.width = prevWidth;
    root.style.minWidth = prevMinWidth;
  });

  // The reflow rules key off these classes, so removing them for the
  // duration of the capture is what actually restores the columns — a
  // container query can't be overridden from script.
  const marked = [root, ...Array.from(root.querySelectorAll("*"))].filter(
    (el): el is HTMLElement =>
      el instanceof HTMLElement &&
      REFLOW_CLASSES.some((cls) => el.classList.contains(cls)),
  );
  for (const el of marked) {
    const removed = REFLOW_CLASSES.filter((cls) => el.classList.contains(cls));
    el.classList.remove(...removed);
    undo.push(() => el.classList.add(...removed));
  }

  return () => {
    // Reverse order so nested overrides unwind cleanly.
    for (let i = undo.length - 1; i >= 0; i--) undo[i]();
  };
}

/**
 * Whether a version's `ui_data` actually carries something renderable.
 *
 * A version can exist with no payload: the worker appends its
 * version_metadata and the API serializes `ui_data` as null when the
 * generation produced nothing. Both renderers treat null as "render
 * nothing", so without this check the card shows its header, date and
 * version dropdown above blank space, with no sign anything went wrong.
 */
export function hasRenderablePayload(uiData: unknown): boolean {
  if (uiData === null || uiData === undefined) return false;
  if (typeof uiData === "string") return uiData.trim() !== "";
  if (typeof uiData === "object" && !Array.isArray(uiData)) {
    return Object.keys(uiData as object).length > 0;
  }
  if (Array.isArray(uiData)) return uiData.length > 0;
  return true;
}
